import { ResultStatus } from '@dispatch/shared/types';
import { QueryKey, useMutation, useQueryClient } from 'react-query';

export function useMutate<
  TQueryFnData = unknown,
  TQueryFnVariables = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  mutationFn: (variables: TQueryFnVariables) => Promise<TData>,
  responseFn: (data: TData | unknown) => Promise<unknown> | void
) {
  const queryClient = useQueryClient();
  const { mutate, error, ...rest } = useMutation(mutationFn, {
    mutationKey: queryKey,
    onSuccess: (data) => {
      responseFn(data);
    },
    onError: (errors) => {
      responseFn(errors);
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  return {
    mutate,
    error,
    status: mapQueryToStatus(rest),
  };
}

export function mapQueryToStatus(result: {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}): ResultStatus {
  if (result.isLoading) {
    return 'loading';
  }

  if (result.isSuccess) {
    return 'success';
  }

  return 'error';
}
